import { useEffect, useState } from "react";
import cn from "classnames";
import { PeriodSelector } from "@/components";
import {
  IAvailableReviewPeriod,
  useGetAvailableReviewPeriods,
  useGetPeriodSkills,
  useGetPeriodSummary,
  useGetPeriodValueDelivered,
} from "@/service/review-period";
import { BackButton, Button } from "@/shared/ui";
import { SkillsMatrixTable, SummarySection } from "@/pages/assessment/_components";
import { getRatingColor } from "@/service/team-assessment";

interface PerformanceReviewStepProps {
  isLoadingData: boolean;
  onClickStart: () => void;
  isTeamLead?: boolean;
}

export const PerformanceReviewStep = ({ isLoadingData, onClickStart, isTeamLead }: PerformanceReviewStepProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<IAvailableReviewPeriod>();

  const { data: periods = [], isLoading: isLoadingPeriods } = useGetAvailableReviewPeriods();

  const periodIdForFetch = selectedPeriod?.period_state === "completed" ? selectedPeriod?.period_id : undefined;

  const { data: summary, isLoading: isLoadingSummary, isError: isErrorSummary } = useGetPeriodSummary(periodIdForFetch);
  const { data: skills, isLoading: isLoadingSkills, isError: isErrorSkills } = useGetPeriodSkills(periodIdForFetch);
  const {
    data: valueDelivered,
    isLoading: isLoadingValueDelivered,
    isError: isErrorValueDelivered,
  } = useGetPeriodValueDelivered(periodIdForFetch);
  useEffect(() => {
    if (periods.length > 0 && !selectedPeriod) {
      setSelectedPeriod(periods[0]);
    }
  }, [periods, selectedPeriod]);

  const isLoadingContent = isLoadingSummary || isLoadingSkills || isLoadingPeriods || isLoadingValueDelivered;
  const isDataEmpty = (isErrorSummary && isErrorSkills) || skills?.dimensions.length === 0 || isErrorValueDelivered;

  return (
    <div data-testid="pr-page" className="container-wrapper flex w-full flex-col">
      <BackButton />
      <h1 data-testid="pr-page-title" className="font-family-third text-eerie-black mt-6 text-[40px]/[120%] font-bold">
        Performance Review
      </h1>
      <div className="mt-6 rounded-4xl bg-white p-8 shadow-[0_0_32px_-8px_rgba(165,44,161,0.12)]">
        {!isTeamLead && (
          <div className="mb-6 flex gap-2">
            <PeriodSelector periods={periods} selectedPeriod={selectedPeriod} onSelectPeriod={setSelectedPeriod} />
          </div>
        )}
        {!isLoadingContent && !isDataEmpty && selectedPeriod?.period_state !== "completed" && (
          <>
            <h2 data-testid="pr-assessment-title" className="font-family-third mb-10 text-[32px] font-semibold">
              Evaluation survey{" "}
              {new Date(selectedPeriod?.period_end).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
            </h2>
            <Button
              data-testid="pr-start-survey-button"
              size="sm"
              disabled={isLoadingData}
              isLoading={isLoadingData}
              appearance="brand"
              className="min-h-[60px] w-1/2"
              onClick={onClickStart}
            >
              Start Survey
            </Button>
          </>
        )}
        {!isLoadingPeriods && summary && skills && selectedPeriod?.period_state !== "not_started" && (
          <>
            <div className="mb-8 flex flex-col justify-between md:flex-row">
              <h2 data-testid="pr-results-title" className="font-family-third text-2xl font-semibold">
                {selectedPeriod?.label}:
                <span className={cn("pl-2", getRatingColor(summary?.overall_rating || ""))}>
                  {summary?.overall_rating}
                </span>
              </h2>
              {/*<ActionButtons periodId={selectedPeriod?.period_id} isDisabled={isLoadingContent || isDataEmpty} />*/}
            </div>
            <SummarySection summary={summary} skills={skills} isLoading={isLoadingSummary} />
            <div className="mt-6">
              <SkillsMatrixTable skills={skills} isLoading={isLoadingSkills} valueDelivered={valueDelivered} />
            </div>
          </>
        )}
        {isDataEmpty && !isLoadingContent && <p className="text-center text-gray-400">No data available</p>}
      </div>
    </div>
  );
};
